import { OptionWrapper } from "@/components/core-components/organisms/Dropdown/components/Option/option.styles";
import {
  Dropdown,
  DropdownModes,
  Sizes as DropdownSizes,
} from "@/components/core-components/organisms/Dropdown/dropdown";
import {
  Button,
  Checkbox,
  Divider,
  Radio,
  ShapeIcon,
  ShapeIconProps,
  Typography,
} from "@flash-tecnologia/hros-web-ui-v2";
import { Box } from "src/components/core-components/atoms/Box/box";
import { CustomTriggerAccordionGroup } from "src/components/core-components/organisms/CustomTriggerAccordionGroup/custom-trigger-accordion-group";
import { Drawer } from "src/components/core-components/organisms/Drawer/drawer";
import { useTheme } from "styled-components";
import { usePermissionsDrawer } from "../../hooks/use-permissions-drawer";
import { PermissionProfiles, PermissionRole } from "../../types";
import * as SC from "./permissions-drawer.style";

interface PermissionsDrawerProps {
  defaultPermissionProfile?: PermissionProfiles;
  defaultPermissionRoles?: PermissionRole[];
  open: boolean;
  onClose: () => void;
  onSubmit: (
    permissionProfile: PermissionProfiles,
    permissionRoles?: PermissionRole[]
  ) => void;
}

export const PermissionsDrawer = ({
  defaultPermissionProfile,
  defaultPermissionRoles,
  open,
  onClose,
  onSubmit,
}: PermissionsDrawerProps) => {
  const theme = useTheme();

  const {
    allPermissionRolesSelected,
    permissionRolesError,
    permissionRolesOptions,
    selectedPermissionProfile,
    selectedPermissionRoles,
    onAllPermissionRolesChange,
    onPermissionProfileChange,
    onPermissionRolesBlur,
    onPermissionRolesChange,
    onSubmitClick,
    resetForm,
  } = usePermissionsDrawer({
    defaultPermissionProfile,
    defaultPermissionRoles,
    onSubmit,
  });

  return (
    <Drawer
      title="Escolha um nível de permissionamento"
      subtitle="Selecione uma opção para continuar"
      open={open}
      onClose={() => {
        resetForm();
        onClose();
      }}
    >
      <Box flexDirection="column" gap="xs" $height={"100%"} $pb="xs4">
        <Box flex={1}>
          <SC.AccordionGroupWrapper
            type="single"
            value={selectedPermissionProfile}
          >
            <Box gap="xs2" flexDirection="column">
              {[
                {
                  iconName: "IconUser" as ShapeIconProps["name"],
                  iconVariant: "default" as ShapeIconProps["variant"],
                  title: "Perfil de colaborador",
                  description:
                    "Acesso as funcionalidades da conta de colaborador.",
                  permissionProfile: PermissionProfiles.EMPLOYEE,
                },
                {
                  iconName: "IconCrown" as ShapeIconProps["name"],
                  iconVariant: "default" as ShapeIconProps["variant"],
                  title: "Super Administrador",
                  description:
                    "Acesso total as empresas do grupo, incluindo permissões e dados da equipe.",
                  permissionProfile: PermissionProfiles.SUPER_ADMINISTRATOR,
                },
                {
                  iconName: "IconBuildingSkyscraper" as ShapeIconProps["name"],
                  iconVariant: "default" as ShapeIconProps["variant"],
                  title: "Administrador de módulo",
                  description:
                    "Tem acesso às permissões, dados da equipe da empresa atual e aos módulos selecionados.",
                  permissionProfile: PermissionProfiles.ADMINISTRATOR,
                  content: (
                    <Box
                      flexDirection="column"
                      gap="xs3"
                      $pt="xs3"
                      $px="xs2"
                      $pb="xs"
                    >
                      <Divider orientation="horizontal" />
                      <Dropdown.Root
                        label={"Selecionar módulos"}
                        error={!!permissionRolesError}
                        mode={DropdownModes.MultiSelect}
                        size={DropdownSizes.Lg}
                        helperText={permissionRolesError}
                        selectedOptions={selectedPermissionRoles?.map(
                          (permissionRole) => ({
                            id: permissionRole.id,
                            label: permissionRole.name,
                            value: permissionRole,
                          })
                        )}
                        onBlur={onPermissionRolesBlur}
                      >
                        <Box
                          $width={"100%"}
                          $minHeight={"300px"}
                          $height={"100%"}
                          flexDirection="column"
                          gap="xs4"
                        >
                          <Dropdown.OptionsWrapper>
                            {permissionRolesOptions?.length ? (
                              <>
                                <OptionWrapper
                                  checked={allPermissionRolesSelected}
                                >
                                  <Box
                                    alignItems="center"
                                    gap="xs3"
                                    $minWidth={0}
                                  >
                                    <Checkbox
                                      checked={allPermissionRolesSelected}
                                      onChange={(_, checked) =>
                                        onAllPermissionRolesChange(checked)
                                      }
                                    />
                                    <Typography.Body3
                                      color={
                                        allPermissionRolesSelected
                                          ? "secondary.40"
                                          : "neutral.30"
                                      }
                                      weight={600}
                                    >
                                      {`Selecionar todos (${permissionRolesOptions.length})`}
                                    </Typography.Body3>
                                  </Box>
                                </OptionWrapper>
                                <Divider orientation="horizontal" />
                              </>
                            ) : (
                              <></>
                            )}
                            {permissionRolesOptions?.map(
                              ({ id: optionId, label, value }) => {
                                const checked = selectedPermissionRoles?.some(
                                  ({ id }) => id === optionId
                                );
                                return (
                                  <Dropdown.Option
                                    key={optionId}
                                    checked={checked}
                                    label={label}
                                    id={optionId}
                                    value={value}
                                  >
                                    <Box
                                      alignItems="center"
                                      gap="xs3"
                                      $minWidth={0}
                                    >
                                      <Checkbox
                                        checked={checked}
                                        onChange={(_, checked) => {
                                          onPermissionRolesChange(checked, {
                                            id: optionId,
                                            label,
                                            value,
                                          });
                                        }}
                                      />
                                      <Typography.Body3
                                        color={
                                          checked
                                            ? "secondary.40"
                                            : "neutral.30"
                                        }
                                        weight={600}
                                      >
                                        {label}
                                      </Typography.Body3>
                                    </Box>
                                  </Dropdown.Option>
                                );
                              }
                            )}
                          </Dropdown.OptionsWrapper>
                        </Box>
                      </Dropdown.Root>
                    </Box>
                  ),
                },
              ].map(
                ({
                  content,
                  iconName,
                  iconVariant,
                  description,
                  title,
                  permissionProfile,
                }) => {
                  return (
                    <CustomTriggerAccordionGroup.Item
                      id={permissionProfile}
                      key={permissionProfile}
                    >
                      <CustomTriggerAccordionGroup.Header>
                        <Box
                          alignItems="center"
                          gap="xs4"
                          flex={1}
                          $px="xs2"
                          $py="xs2"
                        >
                          <ShapeIcon
                            name={iconName}
                            size={32}
                            variant={iconVariant}
                            color={theme.colors.secondary[50]}
                          />
                          <Box flex={1} flexDirection="column" $minWidth={0}>
                            <Typography.Body4 weight={700} color="neutral.30">
                              {title}
                            </Typography.Body4>
                            <Typography.Body4 weight={400} color="neutral.30">
                              {description}
                            </Typography.Body4>
                          </Box>
                          <CustomTriggerAccordionGroup.Trigger asChild>
                            <Radio
                              name={permissionProfile}
                              onChange={() =>
                                onPermissionProfileChange(permissionProfile)
                              }
                              checked={
                                selectedPermissionProfile === permissionProfile
                              }
                            />
                          </CustomTriggerAccordionGroup.Trigger>
                        </Box>
                      </CustomTriggerAccordionGroup.Header>
                      <CustomTriggerAccordionGroup.Content>
                        {content}
                      </CustomTriggerAccordionGroup.Content>
                    </CustomTriggerAccordionGroup.Item>
                  );
                }
              )}
            </Box>
          </SC.AccordionGroupWrapper>
        </Box>
        <Box $width={"100%"} justifyContent="flex-end">
          <Button variant="primary" onClick={onSubmitClick}>
            Salvar
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};
